import type { Attempt, CriterionScore } from "../Questions/types"

export type PaperQuestionStatus = "unanswered" | "answered" | "marked"

export type PaperQuestionSource = "bank" | "ai"

export type PaperSessionStatus =
  | "preparing"
  | "not_started"
  | "in_progress"
  | "submitted"
  | "marked"
  | "abandoned"
  | "failed"

export interface PaperQuestionState {
  questionId: string
  questionText: string
  topic: string
  answerText: string
  timeSpentSeconds: number
  status: PaperQuestionStatus
  score: number | null
  maxScore: number
  source?: PaperQuestionSource
  questionArtifacts?: Array<{
    artifactId: string
    objectKey: string
    downloadUrl?: string | null
    filename?: string | null
    contentType?: string | null
  }>
  criteriaBreakdown?: CriterionScore[] | null
  strengths?: string[]
  improvements?: string[]
  suggestedAnswer?: string | null
  examinerFeedback?: string | null
  confidence?: Attempt["confidence"]
}

export interface PaperSession {
  id: string
  paperId: string
  subjectId: string
  subjectTitle: string
  title?: string
  questions: PaperQuestionState[]
  currentQuestionIndex: number
  status: PaperSessionStatus
  startedAt: string | null
  submittedAt?: string | null
  totalTimeSpentSeconds: number
  lastActiveAt: string
  /** Optimistic concurrency — required on save / exit / submit */
  revision?: number
  /** Live sit token while the exam tab is active */
  sitToken?: string | null
  sitTokenExpiresAt?: string | null
  totalScore?: number | null
  totalMarks?: number
  requestedCount?: number
  questionCount?: number
  aiPendingCount?: number
  useAi?: boolean
  assessmentStatus?: "queued" | "processing" | "completed" | "failed" | null
  assessmentError?: string | null
}

export type GeneratorStep = 1 | 2 | 3 | 4 | 5

export interface PaperTemplate {
  id?: string
  title: string
  subjectId?: string
  subjectTitle: string
  count: number
  time: string
  tags: string[]
}

export const QUESTION_COUNT_OPTIONS = [
  { count: 5, label: "5", sub: "Short Test" },
  { count: 10, label: "10", sub: "Standard" },
  { count: 20, label: "20", sub: "Full Mock" },
] as const

export const MIN_QUESTION_COUNT = 1
export const MAX_QUESTION_COUNT = 50
export const MAX_PAPER_TITLE_LEN = 80

export const paperDisplayTitle = (
  paper: Pick<PaperSession, "title" | "subjectTitle">
) => (paper.title || paper.subjectTitle || "Practice paper").trim()

export const paperPdfDownloadName = (
  paper: Pick<PaperSession, "title" | "subjectTitle">
) => {
  const raw = paperDisplayTitle(paper)
  const safe =
    raw.replace(/[<>:"/\\|?*]+/g, " ").replace(/\s+/g, " ").trim() ||
    "practice-paper"
  return `${safe}.pdf`
}

export const clampQuestionCount = (value: number): number =>
  Math.min(MAX_QUESTION_COUNT, Math.max(MIN_QUESTION_COUNT, Math.round(value)))

export const isValidQuestionCount = (value: number): boolean =>
  Number.isInteger(value) &&
  value >= MIN_QUESTION_COUNT &&
  value <= MAX_QUESTION_COUNT
